// SOR-618: card flip — a live stream's session data on the back of its card.
//
// The trigger is a 26px glyph that fades in on hover/focus, NEVER the card
// body: the whole card is the watch link (B-1), so the glyph stops
// propagation, and flipping on hover would fire constantly while someone
// scans twelve cards.
//
// Motion: single-axis 520ms rotateY under 1100px perspective with an ease
// that settles rather than bounces. Under prefers-reduced-motion the
// rotation is replaced by a 180ms cross-fade. Whichever face is hidden is
// aria-hidden + inert, so the watch link behind the stats can never take
// focus or clicks; Esc closes and hands focus back to the glyph.
import React, { useEffect, useRef, useState } from 'react';
import { IconChartBar } from '@tabler/icons';
import SessionStats from './SessionStats';

const prefersReducedMotion = () =>
  typeof window !== 'undefined' &&
  !!window.matchMedia &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/**
 * @param {Object} props
 * @param {import('./stream').Stream} props.stream  must be live
 * @param {React.ReactNode} props.children  the front face (the card)
 */
const StatsFlip = ({ stream, children }) => {
  const [flipped, setFlipped] = useState(false);
  const [opened, setOpened] = useState(false); // back face mounts on 1st flip
  const glyphRef = useRef(null);
  const backRef = useRef(null);
  const restoreFocus = useRef(false);
  const reduced = prefersReducedMotion();

  const open = () => {
    setOpened(true);
    setFlipped(true);
  };
  const close = () => {
    restoreFocus.current = true;
    setFlipped(false);
  };

  // Focus moves AFTER the commit: until then the target face still carries
  // inert, and browsers silently refuse focus() into inert subtrees (React
  // 18 batches the state update, so a synchronous focus() in close() lands
  // on <body> in every inert-implementing browser — jsdom can't catch it).
  useEffect(() => {
    if (!flipped) {
      if (restoreFocus.current) {
        restoreFocus.current = false;
        glyphRef.current?.focus();
      }
      return undefined;
    }
    backRef.current?.querySelector('button')?.focus();
    const onKey = (e) => {
      if (e.key === 'Escape') close();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [flipped]);

  // React 18 has no `inert` prop; the empty-string trick sets the attribute.
  const inert = (hidden) => (hidden ? '' : undefined);

  const inner = reduced
    ? 'relative h-full'
    : 'relative h-full transition-transform duration-[520ms] ease-[cubic-bezier(0.2,0.7,0.2,1)] [transform-style:preserve-3d]' +
      (flipped ? ' [transform:rotateY(180deg)]' : '');
  const front = reduced
    ? `relative transition-opacity duration-180 ${flipped ? 'opacity-[0]' : 'opacity-[1]'}`
    : 'relative [backface-visibility:hidden]';
  const back =
    (reduced
      ? `absolute inset-0 transition-opacity duration-180 ${flipped ? 'opacity-[1]' : 'opacity-[0]'}`
      : 'absolute inset-0 [transform:rotateY(180deg)] [backface-visibility:hidden]') +
    // Belt over the inert braces: the hidden back face is a full-card
    // overlay in the reduced-motion branch, and inert alone predates some
    // still-supported browsers — never let it eat clicks meant for the link.
    (flipped ? '' : ' pointer-events-none');

  return (
    <div className="group relative h-full [perspective:1100px]">
      <div className={inner}>
        <div aria-hidden={flipped || undefined} inert={inert(flipped)} className={front}>
          {children}
          <button
            ref={glyphRef}
            type="button"
            aria-expanded={flipped}
            aria-label="Show session stats"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              open();
            }}
            // opacity-[0], NEVER opacity-0: the legacy screens bundle
            // Bootstrap, whose .opacity-0 is `opacity:0!important` and pins
            // the glyph invisible over any Tailwind reveal. The arbitrary
            // spelling emits .opacity-\[0\], a class Bootstrap can't match.
            // (Prefixed tokens like group-hover:opacity-100 are safe — the
            // class TOKEN differs from Bootstrap's .opacity-100.)
            className="absolute right-2.25 top-2.25 z-10 flex h-[26px] w-[26px] items-center justify-center rounded-badge bg-browse-scrim text-white opacity-[0] transition-[opacity,transform] duration-180 focus-visible:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-browse-accent group-hover:translate-y-[-2px] group-hover:opacity-100 motion-reduce:transition-none [@media(hover:none)]:opacity-100"
          >
            <IconChartBar size={16} stroke={2} />
          </button>
        </div>
        <div aria-hidden={!flipped || undefined} inert={inert(!flipped)} className={back} ref={backRef}>
          {opened && (
            <SessionStats
              streamId={stream.id}
              viewerCount={stream.viewerCount}
              startsAt={stream.startsAt}
              onClose={close}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default StatsFlip;
